import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { RefreshCw, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Poll the public /system/version endpoint and prompt the user to refresh
// when the server's currently-published version differs from the one this
// session was loaded with.
//
// - Stores the baseline in component state on first successful fetch.
// - Polls every 2 minutes (no auth required by the endpoint).
// - Banner dismissal is per-version: if the user clicks X, we remember that
//   specific version in sessionStorage; banner re-appears on the next deploy.
const POLL_MS = 2 * 60 * 1000;

const VersionBanner = () => {
  const [baseline, setBaseline] = useState(null);
  const [latest, setLatest] = useState(null);
  const [dismissedFor, setDismissedFor] = useState(() => {
    try {
      return sessionStorage.getItem('versionBannerDismissed') || null;
    } catch (e) {
      return null;
    }
  });
  const fetchedOnce = useRef(false);

  const fetchVersion = useCallback(async () => {
    try {
      // Cache-buster query param ensures we always read fresh from the server,
      // even through aggressive CDN / browser caching layers.
      const res = await axios.get(`${API}/system/version?_=${Date.now()}`, { timeout: 8000 });
      const v = res.data?.version || null;
      if (!fetchedOnce.current) {
        fetchedOnce.current = true;
        setBaseline(v);
      }
      setLatest(v);
    } catch (err) {
      // Silent — banner is non-critical; don't spam toasts.
    }
  }, []);

  useEffect(() => {
    fetchVersion();
    const t = setInterval(fetchVersion, POLL_MS);
    // Also re-check whenever the user comes back to the tab — this gives
    // returning users a fast notice without having to wait for the 2-minute
    // poll cycle.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') fetchVersion();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchVersion]);

  const shouldShow = Boolean(
    baseline !== null &&
    latest &&
    latest !== baseline &&
    latest !== dismissedFor
  );

  if (!shouldShow) return null;

  const handleRefresh = () => {
    // Hard-reload to bypass cached bundles.
    try { window.location.reload(true); } catch (e) { window.location.reload(); }
  };

  const handleDismiss = () => {
    try { sessionStorage.setItem('versionBannerDismissed', latest); } catch (e) {}
    setDismissedFor(latest);
  };

  return (
    <div
      className="fixed top-0 inset-x-0 z-[9999] bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg"
      data-testid="version-banner"
      role="alert"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 text-sm">
          <RefreshCw className="w-4 h-4 animate-spin-slow flex-shrink-0" />
          <span>
            A new version <strong data-testid="banner-version">v{latest}</strong> of the HR Portal is available.
            Please refresh to load the latest build.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="px-3 py-1 rounded bg-white text-indigo-700 font-semibold text-xs hover:bg-indigo-50 transition"
            data-testid="banner-refresh-btn"
          >
            Refresh now
          </button>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white"
            aria-label="Dismiss"
            data-testid="banner-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionBanner;
